<template>
  <div
    class="max-w-1600 mx-auto w-full relative"
    :class="[
      {'rounded-t-9 bg-canvas min-h-full' : isPaperShape},
      isDarkMode ? 'text-app-color': 'text-dark-600'
    ]"
  >
    <div
      class="mt-20 md:mt-0 px-20 md:pr-40 pb-150 mx-auto min-h-full flex-grow flex flex-col h-full"
      :class="[
        isPoseidonTheme ? 'bg-notebooks-poseidon' : '',
        isPaperShape ? 'rounded-t-9 pt-40 md:pl-40 bg-canvas' : 'pt-31 md:pl-25 bg-book'
      ]"
    >
      <!-- header -->
      <div class="mb-40 md:flex justify-between items-start relative">
        <div>
          <h1 class="text-26 font-bold leading-none">Task board</h1>
          <div class="mt-4" :class="isDarkMode ? 'text-app-color' : 'text-dark-600'">
            <p v-if="todoTasksLength > 0" class="text-16">There are <span class="font-bold">{{ todoTasksLength }} upcoming</span> tasks</p>
            <p v-else class="text-16">There are no upcoming tasks</p>
          </div>
        </div>
        <FilterModal :tags="filteredTags" @toggleTag="filterByTag" />
      </div>

      <div class="container h-full">
        <TaskList
          v-for="(item, index) of taskLists"
          :key="item.id"
          :item="item"
          :class="`task-list-${index}`"
          class="md:min-h-200 h-full"
          @getTasks="getTasks"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FilterModal from '@/components/task-board/FilterModal'
import TaskList from '@/components/task-board/TaskList'

import { defineComponent, computed, onMounted, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: { FilterModal, TaskList },

  setup () {
    const { state, getters, commit, dispatch } = useStore()
    const route = useRoute()

    const books = computed(() => state.book.books.filter(b => !b.isSection))
    const headquarters = computed(() => state.team.headquarters)
    const settings = computed(() => state.settings.settings)
    const showSidebar = computed(() => state.settings.showSidebar)
    const isPoseidonTheme = computed(() => state.settings.settings.theme === 'Poseidon')
    const isDarkMode = computed(() => getters.isDarkMode)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isPaperShape = computed(() => getters.isPaperShape)
    const tasks = computed(() => state.task.tasks)

    const options = computed(() => {
      return books.value.map(book => {
        return {
          id: book.id,
          title: book.title
        }
      })
    })

    const todoTasksLength = computed(() => tasks.value.filter(item => item.position === '0').length)

    let tagIds = null

    const bookId = computed(() => parseInt(route.params.id))

    const filteredTags = computed(() => {
      const usedTags = {}

      tasks.value.forEach(t => {
        usedTags[t.bookId] = []
      })

      tasks.value.forEach(t => {
        const ids = new Set(usedTags[t.bookId].map(tag => tag.id))

        usedTags[t.bookId] = [...usedTags[t.bookId], ...t.taskTags.filter(d => !ids.has(d.id))]
      })

      return usedTags
    })

    watch(() => route.params.id, (value) => {
      if (value) {
        dispatch('getTasks', { tagIds, bookIds: [route.params.id] })
      }
    })

    onMounted(() => {
      nextTick(() => {
        const allBooks = books.value

        if (isTeamAccount.value) allBooks.push(headquarters.value)

        dispatch('getTaskTags', allBooks.map(b => b.id))
        dispatch('getBook', route.params.id).then(res => {
          if (isTeamAccount.value && res.id && res.chat?.id) {
            dispatch('getMessages', {
              bookId: res.id,
              chatId: res.chat.id
            })
          }
        })
        dispatch('getTasks', { tagIds, bookIds: [route.params.id] })
      })
    })

    const taskLists = computed(() => {
      return [
        { id: 0, name: 'To do' },
        { id: 1, name: 'In progress' },
        { id: 2, name: 'In review' },
        { id: 3, name: 'Done' }
      ]
    })

    async function getTasks () {
      commit('toggleBookLoading', true)
      await dispatch('getTasks', { tagIds, bookIds: [bookId.value] })
      commit('toggleBookLoading', false)
    }

    function filterByTag (ids) {
      tagIds = ids
      dispatch('getTasks', {
        bookIds: [bookId.value],
        tagIds: ids
      }).finally(_ => commit('toggleBookLoading', false))
    }

    return {
      // computed
      filteredTags,
      options,
      settings,
      taskLists,
      isDarkMode,
      showSidebar,
      isPaperShape,
      isPoseidonTheme,
      todoTasksLength,
      // functions
      filterByTag,
      getTasks
    }
  }
})
</script>

<style scoped>
.container {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
  "task-list-0"
  "task-list-1"
  "task-list-2"
  "task-list-3"
  "upcomings";
  gap: 68px 30px;
  grid-auto-flow: row;
}

.task-list-0 { grid-area: task-list-0; }
.task-list-1 { grid-area: task-list-1; }
.task-list-2 { grid-area: task-list-2; }
.task-list-3 { grid-area: task-list-3; }

@media (min-width: 575px) {
  .container {
    grid-template-columns: calc(50% - 16px) calc(50% - 16px);

    grid-template-areas:
      "task-list-0 task-list-1"
      "task-list-2 task-list-3"
  }
}

@media (min-width: 1150px) {
  .container {
    grid-template-columns: calc(33% - 17px) calc(33% - 17px) calc(33% - 17px);

    grid-template-areas:
      "task-list-0 task-list-1 task-list-2"
      "task-list-3 . ."
  }
}

@media (min-width: 1440px) {
  .container {
    grid-template-columns: calc(25% - 23px) calc(25% - 23px) calc(25% - 23px) calc(25% - 23px);

    grid-template-areas:
      "task-list-0 task-list-1 task-list-2 task-list-3"
  }
}

</style>
